<template>
  <v-dialog v-model="opened" max-width="686" persistent>
    <v-card>
      <v-card-title class="headline">
        <v-icon left>mdi-camera</v-icon>
        Ambil Foto
      </v-card-title>
      <v-card-text>
        <div class="camera-wrapper">
          <div v-if="!capturedImage" class="camera-container">
            <video 
              ref="video" 
              autoplay 
              muted 
              playsinline
              class="camera-video"
            ></video>
            <!-- Square overlay guide -->
            <!-- <div class="square-overlay">
              <div class="overlay-border"></div>
            </div> -->
          </div>          
          <div v-if="capturedImage" class="captured-image-wrapper">
            <img :src="capturedImage" alt="Captured" class="captured-image" />
          </div>
          
          <canvas ref="canvas" style="display: none;"></canvas>
          
          <div v-if="error" class="error-message">
            <v-alert type="error" dense>{{ error }}</v-alert>
          </div>
        </div>

        <div class="overlay-text">Posisikan wajah dalam kotak</div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="close"
        >
          <v-icon left>mdi-close</v-icon>
          Tutup
        </v-btn>
        <v-btn
          v-if="capturedImage"
          color="orange"
          text
          @click="retakePhoto"
        >
          <v-icon left>mdi-camera-retake</v-icon>
          Ambil Ulang
        </v-btn>
        <v-btn
          v-if="!capturedImage"
          color="primary"
          @click="capturePhoto"
          :disabled="!cameraReady"
        >
          <v-icon left>mdi-camera</v-icon>
          Ambil Foto
        </v-btn>
        <v-btn
          v-if="capturedImage"
          color="success"
          @click="usePhoto"
        >
          <v-icon left>mdi-check</v-icon>
          Gunakan Foto
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'CameraCapture',
  data() {
    return {
      opened: false,
      cameraReady: false,
      capturedImage: null,
      error: '',
      stream: null
    };
  },
  methods: {
    async open() {
      this.opened = true;
      this.error = '';
      this.capturedImage = null;
      await this.startCamera();
    },
    
    close() {
      this.stopCamera();
      this.opened = false;
      this.capturedImage = null;
      this.error = '';
    },
    
    async startCamera() {
      try {
        this.cameraReady = false;
        
        const constraints = {
          video: {
            width: { ideal: 1920, min: 720 },
            height: { ideal: 1920, min: 720 },
            aspectRatio: { ideal: 1.0 }, // Square aspect ratio
            facingMode: 'user', // Front camera preferred for profile photos
            frameRate: { ideal: 30 }
          }
        };
        
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        
        // Wait for next tick to ensure refs are available
        await this.$nextTick();
        
        if (this.$refs.video) {
          this.$refs.video.srcObject = this.stream;
          this.$refs.video.onloadedmetadata = () => {
            this.cameraReady = true;
          };
        }
      } catch (err) {
        if (err.name === 'NotAllowedError') {
          this.error = 'Akses kamera ditolak. Silakan izinkan akses kamera dan coba lagi.';
        } else if (err.name === 'NotFoundError') {
          this.error = 'Kamera tidak ditemukan.';
        } else {
          this.error = 'Gagal mengakses kamera: ' + err.message;
        }
      }
    },
    
    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      this.cameraReady = false;
    },
    
    capturePhoto() {
      if (!this.$refs.video || !this.$refs.canvas) {
        return;
      }
      
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      const context = canvas.getContext('2d');
      
      // Get the actual video dimensions
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;
      
      // Calculate square dimensions (use the smaller dimension to ensure we don't crop too much)
      const squareSize = Math.min(videoWidth, videoHeight);
      
      // Calculate crop position to center the square
      const cropX = (videoWidth - squareSize) / 2;
      const cropY = (videoHeight - squareSize) / 2;
      
      // Set canvas to high resolution square
      const outputSize = Math.min(1080, squareSize); // Max 1080px for optimal file size
      canvas.width = outputSize;
      canvas.height = outputSize;
      
      // Enable image smoothing for better quality
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      
      // Draw the cropped square video frame to canvas
      context.drawImage(
        video,
        cropX, cropY, squareSize, squareSize, // Source rectangle (square crop from video)
        0, 0, outputSize, outputSize // Destination rectangle (canvas)
      );
      
      // Convert canvas to base64 image with high quality
      this.capturedImage = canvas.toDataURL('image/jpeg', 0.95);
      
      // Stop camera after capture
      this.stopCamera();
    },
    
    retakePhoto() {
      this.capturedImage = null;
      this.startCamera();
    },
    
    async usePhoto() {
      if (!this.capturedImage) {
        return;
      }
      
      try {
        // Convert base64 to blob
        const response = await fetch(this.capturedImage);
        const blob = await response.blob();
        
        // Create a File object from the blob
        const file = new File([blob], `profile-${Date.now()}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
        
        // Emit the captured photo
        this.$emit('photo-captured', file);
        this.close();
      } catch (error) {
        this.$swal.fire({
          icon: 'error',
          title: 'Gagal mengunggah foto',
          text: error?.response?.data?.message || error?.message || 'Terjadi kesalahan saat mengunggah foto.',
          confirmButtonText: 'Tutup'
        });
      }
    }
  },
  
  beforeDestroy() {
    this.stopCamera();
  }
};
</script>

<style scoped>
.camera-wrapper {
  position: relative;
  width: 640px;
  min-height: 640px;
  background: #000;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.camera-container {
  position: relative;
  width: 640px;
  height: 640px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.camera-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.square-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 480px;
  height: 480px;
  pointer-events: none;
  z-index: 10;
}

.overlay-border {
  width: 100%;
  height: 100%;
  border: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  box-shadow: 
    0 0 0 9999px rgba(0, 0, 0, 0.3), /* Dark overlay outside the square */
    inset 0 0 20px rgba(255, 255, 255, 0.2);
}

.overlay-text {
  width: fit-content;
  margin: 16px auto 0 auto;
  color: white;
  font-size: 16px;
  text-align: center;
  background: rgba(0, 0, 0, 0.7);
  padding: 8px 16px;
  border-radius: 20px;
  white-space: nowrap;
}

.captured-image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.captured-image {
  width: 100%;
  max-width: 100%;
  max-height: 640px;
  object-fit: fit;
  border-radius: 16px;
}

.error-message {
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  z-index: 10;
}
</style>
